import React, { useState, useEffect } from 'react';
import { Dialog } from '../../ui';
import { Button } from '../../ui';
import { Badge } from '../../ui';
import { User, Users, Trash2, Plus, X } from 'lucide-react';
import customerAccessService from '../../../services/customerAccessService';
import userManagementService from '../../../services/userManagementService';

/**
 * Manage Access Modal
 * Allows admins/consultants to manage customer owner and assigned consultants
 */
const ManageAccessModal = ({ open, onClose, customer, onUpdate }) => {
  const [owner, setOwner] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [allConsultants, setAllConsultants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const [selectedConsultantId, setSelectedConsultantId] = useState('');

  // Fetch owner, consultants, and available users
  useEffect(() => {
    if (open && customer) {
      fetchData();
    }
  }, [open, customer]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch all users (for owner and consultant selection)
      const usersResponse = await userManagementService.getAllUsers();
      const users = usersResponse?.data || [];

      // Fetch latest customer data to get updated owner_user_id
      const latestCustomer = await customerAccessService.getCustomerById(customer.id);

      // Fetch owner info if exists
      if (latestCustomer.owner_user_id) {
        const ownerUser = users.find(u => u.id === latestCustomer.owner_user_id);
        setOwner(ownerUser || null);
      } else {
        setOwner(null);
      }

      // Fetch consultants assigned to this customer
      const consultantsList = await customerAccessService.getCustomerConsultants(customer.id);
      setConsultants(consultantsList || []);

      // Filter users by role
      setAllUsers(users.filter(u => u.role === 'user'));
      setAllConsultants(users.filter(u => u.role === 'consultant' || u.role === 'admin'));

    } catch (error) {
      console.error('Error fetching access data:', error);
      alert('Failed to load users: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetOwner = async () => {
    if (!selectedOwnerId) return;

    try {
      setSaving(true);
      await customerAccessService.setCustomerOwner(customer.id, selectedOwnerId);

      // Refresh data
      await fetchData();
      setSelectedOwnerId('');

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error setting owner:', error);
      alert(error.message || 'Failed to set owner');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveOwner = async () => {
    if (!window.confirm('Are you sure you want to remove the owner?')) return;

    try {
      setSaving(true);
      await customerAccessService.setCustomerOwner(customer.id, null);

      // Refresh data
      await fetchData();

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error removing owner:', error);
      alert(error.message || 'Failed to remove owner');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignConsultant = async () => {
    if (!selectedConsultantId) return;

    try {
      setSaving(true);
      await customerAccessService.assignConsultant(selectedConsultantId, customer.id);

      // Refresh data
      await fetchData();
      setSelectedConsultantId('');

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error assigning consultant:', error);
      alert(error.message || 'Failed to assign consultant');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveConsultant = async (consultantId) => {
    if (!window.confirm('Are you sure you want to remove this consultant?')) return;

    try {
      setSaving(true);
      await customerAccessService.removeConsultant(consultantId, customer.id);

      // Refresh data
      await fetchData();

      if (onUpdate) {
        onUpdate();
      }
    } catch (error) {
      console.error('Error removing consultant:', error);
      alert(error.message || 'Failed to remove consultant');
    } finally {
      setSaving(false);
    }
  };

  if (!customer) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`Manage Access - ${customer.name}`}
      size="medium"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="py-8 text-center text-gray-500">Loading...</div>
      ) : (
        <div className="space-y-6">
          {/* Owner Section */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Customer Owner</h3>
            </div>

            {owner ? (
              <div className="bg-white rounded-lg p-3 border border-gray-200 mb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">
                        {owner.firstName?.[0]}{owner.lastName?.[0]}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {owner.firstName} {owner.lastName}
                      </div>
                      <div className="text-sm text-gray-600">{owner.email}</div>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={handleRemoveOwner}
                    disabled={saving}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg p-3 border border-gray-200 mb-3">
                <p className="text-sm text-gray-500 italic">No owner assigned</p>
              </div>
            )}

            {/* Set/Change Owner */}
            <div className="flex gap-2">
              <select
                value={selectedOwnerId}
                onChange={(e) => setSelectedOwnerId(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={saving}
              >
                <option value="">Select a user...</option>
                {allUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.firstName} {user.lastName} ({user.email})
                  </option>
                ))}
              </select>
              <Button
                variant="primary"
                onClick={handleSetOwner}
                disabled={!selectedOwnerId || saving}
              >
                <Plus className="w-4 h-4 mr-1" />
                {owner ? 'Change' : 'Set'}
              </Button>
            </div>
          </div>

          {/* Consultants Section */}
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Assigned Consultants</h3>
              <Badge variant="info">{consultants.length}</Badge>
            </div>

            {/* Consultants List */}
            <div className="space-y-2 mb-3">
              {consultants.length > 0 ? (
                consultants.map((consultant) => (
                  <div
                    key={consultant.id}
                    className="bg-white rounded-lg p-3 border border-gray-200"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-semibold">
                            {consultant.firstName?.[0]}{consultant.lastName?.[0]}
                          </span>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {consultant.firstName} {consultant.lastName}
                          </div>
                          <div className="text-sm text-gray-600">{consultant.email}</div>
                        </div>
                      </div>
                      <Button
                        variant="secondary"
                        size="small"
                        onClick={() => handleRemoveConsultant(consultant.id)}
                        disabled={saving}
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <p className="text-sm text-gray-500 italic">No consultants assigned</p>
                </div>
              )}
            </div>

            {/* Add Consultant */}
            <div className="flex gap-2">
              <select
                value={selectedConsultantId}
                onChange={(e) => setSelectedConsultantId(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={saving}
              >
                <option value="">Select a consultant...</option>
                {allConsultants
                  .filter(c => !consultants.find(existing => existing.id === c.id))
                  .map((consultant) => (
                    <option key={consultant.id} value={consultant.id}>
                      {consultant.firstName} {consultant.lastName} ({consultant.email})
                    </option>
                  ))}
              </select>
              <Button
                variant="primary"
                onClick={handleAssignConsultant}
                disabled={!selectedConsultantId || saving}
              >
                <Plus className="w-4 h-4 mr-1" />
                Assign
              </Button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default ManageAccessModal;
